<template>
    <div
        v-if="isOpen"
        class="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center tw:p-4"
    >
        <div
            class="tw:absolute tw:inset-0 tw:bg-black/50 tw:backdrop-blur-sm"
            @click="handleBackdropClick"
        ></div>

        <div :class="panelClasses">
            <div class="tw:flex tw:items-start tw:justify-between tw:p-6 tw:border-b tw:border-gray-100">
                <div class="tw:flex-1 tw:pr-4">
                    <h2 class="tw:text-xl tw:font-semibold tw:text-gray-800">
                        {{ title }}
                    </h2>
                    <p v-if="subtitle" class="tw:mt-1 tw:text-sm tw:text-gray-500 tw:mb-0">
                        {{ subtitle }}
                    </p>
                </div>
                <Button
                    variant="ghost"
                    icon="fas fa-times"
                    customClass="tw:p-2"
                    @click="handleClose"
                />
            </div>

            <div class="tw:max-h-[calc(100vh-12rem)] tw:overflow-y-auto tw:p-6">
                <p v-if="showMessage" class="tw:text-base tw:text-gray-700 tw:mb-0">
                    {{ message }}
                </p>
                <slot></slot>
            </div>

            <div class="tw:flex tw:items-center tw:justify-end tw:gap-3 tw:p-4 tw:px-6 tw:bg-gray-50">
                <Button
                    v-if="showCancel"
                    variant="ghost"
                    @click="handleClose"
                >
                    {{ cancelText }}
                </Button>
                <Button
                    :variant="confirmVariant"
                    :disabled="confirmDisabled"
                    @click="handleConfirm"
                >
                    {{ confirmText }}
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { computed, onMounted, onUnmounted } from 'vue'
    import Button from '@/modules/admin/ui/Button.vue'

    const props = defineProps({
        show: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: 'Confirmar acción'
        },
        subtitle: {
            type: String,
            default: ''
        },
        message: {
            type: String,
            default: '¿Estás seguro de que deseas realizar esta acción?'
        },
        showMessage: {
            type: Boolean,
            default: true
        },
        confirmText: {
            type: String,
            default: 'Confirmar'
        },
        cancelText: {
            type: String,
            default: 'Cancelar'
        },
        confirmVariant: {
            type: String,
            default: 'danger',
            validator: value => ['primary', 'secondary', 'danger', 'warning', 'success'].includes(value)
        },
        showCancel: {
            type: Boolean,
            default: true
        },
        confirmDisabled: {
            type: Boolean,
            default: false
        },
        maxWidth: {
            type: String,
            default: 'md',
            validator: value => ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'].includes(value)
        },
        closeOnBackdrop: {
            type: Boolean,
            default: true
        },
        closeOnEscape: {
            type: Boolean,
            default: true
        }
    })

    const emit = defineEmits(['close', 'confirm'])

    const isOpen = computed(() => props.show)

    const panelClasses = computed(() => {
        const maxWidthClasses = {
            sm: 'tw:max-w-sm',
            md: 'tw:max-w-md',
            lg: 'tw:max-w-lg',
            xl: 'tw:max-w-xl',
            '2xl': 'tw:max-w-2xl',
            '3xl': 'tw:max-w-3xl',
            '4xl': 'tw:max-w-4xl'
        }

        return [
            'tw:relative tw:w-full tw:max-h-[calc(100vh-2rem)] tw:overflow-hidden tw:bg-white tw:rounded-xl tw:shadow-2xl',
            maxWidthClasses[props.maxWidth] || maxWidthClasses.md
        ]
    })

    const handleBackdropClick = () => {
        if (props.closeOnBackdrop) {
            handleClose()
        }
    }

    const handleKeydown = event => {
        if (event.key === 'Escape' && props.closeOnEscape && props.show) {
            handleClose()
        }
    }

    onMounted(() => {
        document.addEventListener('keydown', handleKeydown)
    })

    onUnmounted(() => {
        document.removeEventListener('keydown', handleKeydown)
    })

    const handleClose = () => {
        emit('close')
    }

    const handleConfirm = () => {
        emit('confirm')
    }
</script>
